import { createSelector } from 'reselect';
import type { Location } from 'react-router-dom';

import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { selectIsRegularSiteType } from '~/components/brand/ducks/index';
import {
	gamesByGameCodeSelector as casinoGamesByGameCodeSelector,
	gameNamesByIdxSelector as casinoGameNamesByIdxSelector,
	gameCodesByProviderCodeSelector as casinoGameCodesByProviderCodeSelector,
	gameCodesBySubProviderCodeSelector as casinoGameCodesBySubProviderCodeSelector,
	gamesByNameSelector as casinoGamesByNameSelector,
	providersSelector as casinoProvidersSelector,
	subProvidersSelector as casinoSubProvidersSelector
} from '~/components/casino/ducks/selectors';
import {
	casinoGamesSelector as subnavigationCasinoGamesByGameCodeSelector,
	liveCasinoGamesSelector as subnavigationLiveCasinoGamesByGameCodeSelector
} from '~/components/casino-shared/subnavigation/ducks/selectors';
import {
	gamesByGameCodeSelector as liveCasinoGamesByGameCodeSelector,
	gameNamesByIdxSelector as liveCasinoGamesNamesByIdxSelector,
	gameCodesByProviderCodeSelector as liveCasinoGameCodesByProviderCodeSelector,
	gameCodesBySubProviderCodeSelector as liveCasinoGameCodesBySubProviderCodeSelector,
	gamesByNameSelector as liveCasinoGamesByNameSelector,
	providersSelector as liveCasinoProvidersSelector,
	subProvidersSelector as liveCasinoSubProvidersSelector
} from '~/components/live-casino/ducks/selectors';
import {
	gamesByGameCodeSelector as allGamesByGameCodeSelector,
	gameNamesByIdxSelector as allGamesNamesByIdxSelector,
	gameCodesByProviderCodeSelector as allGamesGameCodesByProviderCodeSelector,
	gameCodesBySubProviderCodeSelector as allGamesGameCodesBySubProviderCodeSelector,
	gamesByNameSelector as allGamesByNameSelector,
	providersSelector as allGamesProvidersSelector,
	subProvidersSelector as allGamesSubProvidersSelector
} from '~/components/casino-shared/all-games/ducks/selectors';
import { EnhancedGame } from '../types';
import type { CasinoSharedRootState } from './types';
import { LIVE_CASINO_PATHS } from '~/components/router/casino-routes';

const REDUCER_NAME = 'casinoShared';

export const gameNamesByIdxSelector = createSelector(
	[casinoGameNamesByIdxSelector, liveCasinoGamesNamesByIdxSelector, allGamesNamesByIdxSelector],
	(casinoGameNamesByIdx, liveCasinoGamesNamesByIdx, allGamesNamesByIdx) => {
		let gameNames: string[] = [];
		if (casinoGameNamesByIdx) {
			gameNames = [...gameNames, ...casinoGameNamesByIdx];
		}
		if (liveCasinoGamesNamesByIdx) {
			gameNames = [...gameNames, ...liveCasinoGamesNamesByIdx];
		}
		if (allGamesNamesByIdx) {
			gameNames = [...gameNames, ...allGamesNamesByIdx];
		}
		// filtering game names to remove empty, undefined
		const filteredGameNames = gameNames.filter(name => name);
		return [...new Set(filteredGameNames)];
	}
);

export const gamesByGameCodeSelector = createSelector(
	[
		casinoGamesByGameCodeSelector,
		liveCasinoGamesByGameCodeSelector,
		allGamesByGameCodeSelector,
		subnavigationCasinoGamesByGameCodeSelector,
		subnavigationLiveCasinoGamesByGameCodeSelector
	],
	(
		casinoGamesByGameCode,
		liveCasinoGamesByGameCode,
		allGamesByGameCode,
		subnavigationCasinoGamesByGameCode,
		subnavigationLiveCasinoGamesByGameCode
	) => ({
		...(casinoGamesByGameCode || {}),
		...(liveCasinoGamesByGameCode || {}),
		...(allGamesByGameCode || {}),
		...(subnavigationCasinoGamesByGameCode || {}),
		...(subnavigationLiveCasinoGamesByGameCode || {})
	})
);

export const gameCodesByProviderCodeSelector = createSelector(
	[
		casinoGameCodesByProviderCodeSelector,
		liveCasinoGameCodesByProviderCodeSelector,
		allGamesGameCodesByProviderCodeSelector
	],
	(casinoGameCodesByProviderCode, liveCasinoGameCodesByProviderCode, allGamesGameCodesByProviderCode) => {
		const gameCodesByProvider: Record<string, string[]> = {};
		if (casinoGameCodesByProviderCode) {
			Object.keys(casinoGameCodesByProviderCode).forEach(providerCode => {
				if (!gameCodesByProvider[providerCode]) {
					gameCodesByProvider[providerCode] = [];
				}
				gameCodesByProvider[providerCode].push(...casinoGameCodesByProviderCode[providerCode]);
			});
		}
		if (liveCasinoGameCodesByProviderCode) {
			Object.keys(liveCasinoGameCodesByProviderCode).forEach(providerCode => {
				if (!gameCodesByProvider[providerCode]) {
					gameCodesByProvider[providerCode] = [];
				}
				gameCodesByProvider[providerCode].push(...liveCasinoGameCodesByProviderCode[providerCode]);
			});
		}
		if (allGamesGameCodesByProviderCode) {
			Object.keys(allGamesGameCodesByProviderCode).forEach(providerCode => {
				if (!gameCodesByProvider[providerCode]) {
					gameCodesByProvider[providerCode] = [];
				}
				gameCodesByProvider[providerCode].push(...allGamesGameCodesByProviderCode[providerCode]);
			});
		}
		return Object.keys(gameCodesByProvider).reduce<Record<string, string[]>>((acc, curr) => {
			acc[curr] = [...new Set(gameCodesByProvider[curr])];
			return acc;
		}, {});
	}
);

export const gameCodesBySubProviderCodeSelector = createSelector(
	[
		casinoGameCodesBySubProviderCodeSelector,
		liveCasinoGameCodesBySubProviderCodeSelector,
		allGamesGameCodesBySubProviderCodeSelector
	],
	(casinoGameCodesBySubProviderCode, liveCasinoGameCodesBySubProviderCode, allGamesGameCodesBySubProviderCode) => {
		const gameCodesBySubProvider: Record<string, string[]> = {};
		if (casinoGameCodesBySubProviderCode) {
			Object.keys(casinoGameCodesBySubProviderCode).forEach(subProviderCode => {
				if (!gameCodesBySubProvider[subProviderCode]) {
					gameCodesBySubProvider[subProviderCode] = [];
				}
				gameCodesBySubProvider[subProviderCode].push(...casinoGameCodesBySubProviderCode[subProviderCode]);
			});
		}
		if (liveCasinoGameCodesBySubProviderCode) {
			Object.keys(liveCasinoGameCodesBySubProviderCode).forEach(subProviderCode => {
				if (!gameCodesBySubProvider[subProviderCode]) {
					gameCodesBySubProvider[subProviderCode] = [];
				}
				gameCodesBySubProvider[subProviderCode].push(...liveCasinoGameCodesBySubProviderCode[subProviderCode]);
			});
		}
		if (allGamesGameCodesBySubProviderCode) {
			Object.keys(allGamesGameCodesBySubProviderCode).forEach(subProviderCode => {
				if (!gameCodesBySubProvider[subProviderCode]) {
					gameCodesBySubProvider[subProviderCode] = [];
				}
				gameCodesBySubProvider[subProviderCode].push(...allGamesGameCodesBySubProviderCode[subProviderCode]);
			});
		}
		return Object.keys(gameCodesBySubProvider).reduce<Record<string, string[]>>((acc, curr) => {
			acc[curr] = [...new Set(gameCodesBySubProvider[curr])];
			return acc;
		}, {});
	}
);

export const gamesByNameSelector = createSelector(
	[casinoGamesByNameSelector, liveCasinoGamesByNameSelector, allGamesByNameSelector],
	(casinoGamesByName, liveCasinoGamesByName, allGamesByName) => {
		let gamesByName: Record<string, EnhancedGame> = {};
		if (casinoGamesByName) {
			gamesByName = { ...gamesByName, ...casinoGamesByName };
		}
		if (liveCasinoGamesByName) {
			gamesByName = {
				...gamesByName,
				...liveCasinoGamesByName
			};
		}
		if (allGamesByName) {
			gamesByName = { ...gamesByName, ...allGamesByName };
		}
		return gamesByName;
	}
);

export const gamesByProviderCodeSelector = createSelector(
	[gameCodesByProviderCodeSelector, gamesByGameCodeSelector],
	(gameCodesByProviderCode, gamesByCode) => {
		return Object.keys(gameCodesByProviderCode).reduce<Record<string, EnhancedGame[]>>((acc, curr) => {
			acc[curr] = gameCodesByProviderCode[curr].map(name => gamesByCode[name]);
			return acc;
		}, {});
	}
);

export const gamesBySubProviderCodeSelector = createSelector(
	[gameCodesBySubProviderCodeSelector, gamesByGameCodeSelector],
	(gameNamesBySubProviderCode, gamesByCode) => {
		return Object.keys(gameNamesBySubProviderCode).reduce<Record<string, EnhancedGame[]>>((acc, curr) => {
			acc[curr] = gameNamesBySubProviderCode[curr].map(name => gamesByCode[name]);
			return acc;
		}, {});
	}
);

export const filterSelector = (_: CasinoSharedRootState, props: { filter: string | undefined }) => props.filter;
export const isLiveCasinoLocationSelector = (
	_: CasinoSharedRootState,
	props: { location: globalThis.Location | Location }
) => LIVE_CASINO_PATHS.some(path => props.location.pathname.includes(path));

export const lovedAndRecentSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.lovedAndRecent;

export const lovedGamesSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.lovedAndRecent?.loved;

export const localRecentlyPlayedSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.localRecentlyPlayed;

export const jackpotDataSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.jackpotData;

export const gameJackpotDataSelector = createSelector(
	jackpotDataSelector,
	(_: CasinoSharedRootState, game: Pick<EnhancedGame, 'provider' | 'publishCode'>) => game,
	(jackpotData, game) => {
		if (
			game?.publishCode &&
			jackpotData?.[game?.publishCode]?.provider &&
			jackpotData?.[game?.publishCode]?.provider === game?.provider?.code.replace('_MOBILE', '')
		) {
			return jackpotData[game?.publishCode].amount;
		}

		return null;
	}
);

export const isInsufficientCasinoBalanceModalShownSelector = (state: CasinoSharedRootState) =>
	state[REDUCER_NAME]?.isInsufficientCasinoBalanceModalShown;

export const isBalanceLimitExceededModalShownSelector = (state: CasinoSharedRootState) =>
	state[REDUCER_NAME]?.isBalanceLimitExceededModalShown;

export const isZeroCasinoBalanceModalShownSelector = (state: CasinoSharedRootState) =>
	state[REDUCER_NAME]?.isZeroCasinoBalanceModalShown;

export const myGamesSelector = createSelector(
	[gamesByGameCodeSelector, lovedAndRecentSelector, filterSelector, isLiveCasinoLocationSelector],
	(gamesByGameCode, lovedAndRecent, filter, isLiveCasinoLocation) => {
		if (!Object.keys(gamesByGameCode).length) return [];

		let filteredGames = Object.values(gamesByGameCode);

		if (isLiveCasinoLocation) {
			filteredGames = filteredGames.filter(game => game.categoryCode === 'CASINO_LIVE_CASINO');
		}
		if (!filter) return filteredGames;
		if (filter === 'loved') {
			return filteredGames.filter(game => lovedAndRecent?.loved?.includes(game.code));
		}
		return filteredGames.filter(game => (filter in game ? game[filter as keyof EnhancedGame] : false));
	}
);

export const hasFunMode = (
	providerCode: string,
	categoryCode: string | undefined,
	blockPlayForFun: boolean,
	subProvider?: string
) => {
	// We prevent games in the fun mode because of two reasons.
	// 1-The game provider doesn't support the fun mode (noFunProvider).
	// 2-The fun mode is disabled for the franchise. (blockPlayForFun).
	// If provider and franchise supports the fun mode return TRUE otherwise FALSE
	const noFunProviders = ['BLACKCOW', 'BLACKCOW_MOBILE', 'SPINON', 'SPINON_MOBILE'];
	const noFunPartialProviders = [
		'PRAGMATIC_PLAY',
		'PRAGMATIC_PLAY_MOBILE',
		'EVOLUTION',
		'EVOLUTION_MOBILE',
		'STAKELOGIC',
		'STAKELOGIC_MOBILE'
	];
	const noFunSubProviders = ['OnAir'];

	const blockJackpotGamesPragmatic =
		['PRAGMATIC_PLAY', 'PRAGMATIC_PLAY_MOBILE'].includes(providerCode) && categoryCode === 'CASINO_JACKPOT';
	const noFunCategoryCodes = ['CASINO_LIVE_CASINO'];

	const noFunMode =
		noFunProviders.includes(providerCode) ||
		(noFunPartialProviders.includes(providerCode) && categoryCode && noFunCategoryCodes.includes(categoryCode)) ||
		blockJackpotGamesPragmatic ||
		(subProvider && noFunSubProviders.includes(subProvider));

	return !(noFunMode || blockPlayForFun);
};

export const blockPlayForFunSelector = (state: CasinoSharedRootState) => state.brand.blockPlayForFun;

export const gameDetailByGameCodeSelector = createSelector(
	gamesByGameCodeSelector,
	blockPlayForFunSelector,
	(_: CasinoSharedRootState, gameCode: string) => gameCode,
	(games, blockPlayForFun, gameCode = '') => {
		return gameCode && games[gameCode]
			? {
					...games[gameCode],
					hasFunMode: hasFunMode(
						games[gameCode]?.provider?.code,
						games[gameCode]?.categoryCode,
						blockPlayForFun,
						games[gameCode]?.casinoSubProviderVO?.name
					)
			  }
			: null;
	}
);

export const hasDepositLoginSelector = createSelector(
	[selectIsRegularSiteType, authenticationSelector],
	(isRegularSiteType, authenticated) => {
		return !authenticated && !isRegularSiteType;
	}
);

export const jackpotsSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.jackpots;

export const activeFiltersSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.activeFilters;
export const activeSortingSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.activeSortingOption;

export const providersSelector = createSelector(
	[casinoProvidersSelector, liveCasinoProvidersSelector, allGamesProvidersSelector],
	(casinoProviders, liveCasinoProviders, allGamesProviders) => {
		return { ...casinoProviders, ...liveCasinoProviders, ...allGamesProviders };
	}
);
export const subProvidersSelector = createSelector(
	[casinoSubProvidersSelector, liveCasinoSubProvidersSelector, allGamesSubProvidersSelector],
	(casinoSubProviders, liveCasinoSubProviders, allGamesSubProviders) => {
		return { ...casinoSubProviders, ...liveCasinoSubProviders, ...allGamesSubProviders };
	}
);
